import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import apiClient from './api';
import {
    Container, Typography, Button, Select, MenuItem,
    FormControl, InputLabel, Stack, TextField, Box,
    CircularProgress, Snackbar, Alert, Autocomplete,
} from '@mui/material';
import { DatePicker } from 'zaman';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventNoteIcon from '@mui/icons-material/EventNote';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import NotesIcon from '@mui/icons-material/Notes';
import PaymentsIcon from '@mui/icons-material/Payments';
import PlaceIcon from '@mui/icons-material/Place';
import ElderlyIcon from '@mui/icons-material/Elderly';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import { formatToman } from './labels';
import { glass, brand } from './theme';
import { SectionHeader } from './components/SectionHeader';
import { CITIES, districtSuggestions } from './locations';

const SERVICE_ICONS = {
    'مراقبت از سالمند': ElderlyIcon,
    'مراقبت از کودک': ChildCareIcon,
    'مراقبت از بیمار': LocalHospitalIcon,
    'پرستاری و تزریقات': MedicalServicesIcon,
    'همراه بیمار در بیمارستان': EmojiPeopleIcon,
    'کمک در کارهای منزل': CleaningServicesIcon,
};

function RequestForm() {
    const navigate = useNavigate();
    const [services, setServices] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);
    const [contractType, setContractType] = useState('daily');
    const [shiftType, setShiftType] = useState('day');
    const [notes, setNotes] = useState('');
    const [city, setCity] = useState('تهران');
    const [district, setDistrict] = useState('');
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [servicesLoading, setServicesLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [estimate, setEstimate] = useState(null);

    useEffect(() => {
        setServicesLoading(true);
        apiClient.get('/api/services/')
            .then(response => setServices(Array.isArray(response.data) ? response.data : []))
            .catch(() => {
                setSnackbar({ open: true, message: 'خطا در دریافت لیست خدمات از سرور.', severity: 'error' });
                setServices([]);
            })
            .finally(() => setServicesLoading(false));
    }, []);

    useEffect(() => {
        const params = new URLSearchParams({
            service_ids: selectedServices.join(','),
            contract_type: contractType,
            shift_type: shiftType,
        });
        apiClient.get(`/api/pricing/estimate/?${params.toString()}`)
            .then((res) => setEstimate(res.data))
            .catch(() => setEstimate(null));
    }, [selectedServices, contractType, shiftType]);

    const toggleService = (serviceId) => {
        setSelectedServices((prev) => (
            prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
        ));
    };

    // کتابخانه‌ی zaman تقویم را بعد از انتخاب تاریخ خودش نمی‌بندد — فقط با کلیک
    // بیرون از تقویم می‌بندد (طبق منطق داخلی خودش). این تابع یک کلیک شبیه‌سازی‌شده
    // روی body می‌زند تا همان مکانیزم داخلی کتابخانه تقویم را ببندد.
    const closeDatePickers = () => {
        setTimeout(() => {
            document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        }, 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!startDate || !endDate) {
            setSnackbar({ open: true, message: 'لطفاً تاریخ شروع و پایان را انتخاب کنید.', severity: 'error' });
            setLoading(false);
            return;
        }

        const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
        if (new Date(startDate) < todayStart) {
            setSnackbar({ open: true, message: 'تاریخ شروع نمی‌تواند در گذشته باشد.', severity: 'error' });
            setLoading(false);
            return;
        }
        if (new Date(endDate) < new Date(startDate)) {
            setSnackbar({ open: true, message: 'تاریخ پایان نمی‌تواند قبل از تاریخ شروع باشد.', severity: 'error' });
            setLoading(false);
            return;
        }

        const requestData = {
            contract_type: contractType,
            shift_type: shiftType,
            start_date: new Date(startDate).toISOString(),
            end_date: new Date(endDate).toISOString(),
            notes: notes,
            city,
            district: district.trim(),
            service_ids: selectedServices,
        };
        try {
            await apiClient.post('/api/requests/', requestData);
            navigate('/my-requests', { state: { justSubmitted: true } });
        } catch (error) {
            const errorDetail = error.response?.data?.detail || error.response?.data?.non_field_errors || ['لطفاً دوباره تلاش کنید.'];
            setSnackbar({ open: true, message: `خطا در ثبت درخواست: ${Array.isArray(errorDetail) ? errorDetail.join(' ') : errorDetail}`, severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') { return; }
        setSnackbar({ ...snackbar, open: false });
    };

    const datePickerInputStyle = {
        padding: '16.5px 14px', border: '1px solid rgba(15,51,43,0.23)', boxSizing: 'border-box',
        borderRadius: '12px', width: '100%', fontFamily: brand.fontDisplay, fontSize: '0.95rem',
        color: brand.ink, background: 'rgba(255,255,255,0.7)', outline: 'none',
    };

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%', pb: 6 }}>
            <Container maxWidth="sm" sx={{ pt: 3 }}>
                <Button
                    component={RouterLink} to="/dashboard" startIcon={<ArrowForwardIcon />}
                    size="small" sx={{ mb: 2, color: 'text.secondary' }}
                >
                    بازگشت به داشبورد
                </Button>

                <Box sx={{ ...glass.card, p: { xs: 2.5, md: 4 } }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, textAlign: 'center' }}>
                        ثبت درخواست جدید
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                        نیاز خود را دقیق مشخص کنید تا بهترین همیارها متوجه درخواستتان شوند.
                    </Typography>

                    <Box component="form" onSubmit={handleSubmit}>
                        <Stack spacing={4}>

                            <Box>
                                <SectionHeader icon={EventNoteIcon} title="نوع همکاری و زمان‌بندی" />
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <FormControl fullWidth>
                                            <InputLabel>نوع قرارداد</InputLabel>
                                            <Select value={contractType} label="نوع قرارداد" onChange={(e) => setContractType(e.target.value)} sx={{ borderRadius: '12px' }}>
                                                <MenuItem value="daily">روزانه</MenuItem>
                                                <MenuItem value="hourly">ساعتی</MenuItem>
                                                <MenuItem value="monthly">ماهانه</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth>
                                            <InputLabel>شیفت کاری</InputLabel>
                                            <Select value={shiftType} label="شیفت کاری" onChange={(e) => setShiftType(e.target.value)} sx={{ borderRadius: '12px' }}>
                                                <MenuItem value="day">روز</MenuItem>
                                                <MenuItem value="night">شب</MenuItem>
                                                <MenuItem value="24h">شبانه‌روزی</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                        <Box sx={{ flex: '1 1 150px' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.75, pr: 0.5 }}>
                                                تاریخ شروع
                                            </Typography>
                                            <DatePicker defaultValue={startDate} onChange={(d) => { setStartDate(d.value); closeDatePickers(); }} round="x2" inputAttributes={{ style: datePickerInputStyle }} />
                                        </Box>
                                        <Box sx={{ flex: '1 1 150px' }}>
                                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.75, pr: 0.5 }}>
                                                تاریخ پایان
                                            </Typography>
                                            <DatePicker defaultValue={endDate} onChange={(d) => { setEndDate(d.value); closeDatePickers(); }} round="x2" inputAttributes={{ style: datePickerInputStyle }} />
                                        </Box>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box>
                                <SectionHeader icon={PlaceIcon} title="محدوده‌ی مراقبت" subtitle="فقط محدوده‌ی تقریبی نمایش داده می‌شود، نه آدرس دقیق" />
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                    <FormControl sx={{ flex: '1 1 150px' }}>
                                        <InputLabel>شهر</InputLabel>
                                        <Select value={city} label="شهر" onChange={(e) => { setCity(e.target.value); setDistrict(''); }} sx={{ borderRadius: '12px' }}>
                                            {CITIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                    <Autocomplete
                                        freeSolo
                                        sx={{ flex: '1 1 200px' }}
                                        options={districtSuggestions(city)}
                                        inputValue={district}
                                        onInputChange={(e, v) => setDistrict(v)}
                                        renderInput={(params) => (
                                            <TextField {...params} label="منطقه یا محله" placeholder="مثلاً منطقه ۵ یا نارمک"
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }} />
                                        )}
                                    />
                                </Box>
                            </Box>

                            <Box>
                                <SectionHeader icon={RoomServiceIcon} title="خدمات مورد نیاز" subtitle="یک یا چند مورد را انتخاب کنید" />
                                {servicesLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
                                ) : services.length > 0 ? (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
                                        {services.map((service) => {
                                            const Icon = SERVICE_ICONS[service.name] || VolunteerActivismIcon;
                                            const selected = selectedServices.includes(service.id);
                                            return (
                                                <Box
                                                    key={service.id}
                                                    onClick={() => toggleService(service.id)}
                                                    sx={{
                                                        cursor: 'pointer', p: 1.5, borderRadius: '14px',
                                                        border: '1.5px solid', borderColor: selected ? brand.primary : 'rgba(15,51,43,0.15)',
                                                        bgcolor: selected ? brand.primaryTint : 'rgba(255,255,255,0.6)',
                                                        display: 'flex', alignItems: 'center', gap: 1,
                                                        transition: 'all 0.15s ease',
                                                        '&:hover': { transform: 'translateY(-2px) scale(1.02)', boxShadow: '0 8px 20px rgba(15,51,43,0.12)' },
                                                        '&:active': { transform: 'scale(0.98)' },
                                                    }}
                                                >
                                                    <Icon sx={{ color: selected ? brand.primary : 'text.secondary', fontSize: 22 }} />
                                                    <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 500, flexGrow: 1 }}>{service.name}</Typography>
                                                    {selected && <CheckCircleIcon sx={{ color: brand.primary, fontSize: 18 }} />}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Typography color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
                                        هیچ خدمتی یافت نشد.
                                    </Typography>
                                )}
                            </Box>

                            <Box>
                                <SectionHeader icon={NotesIcon} title="شرح درخواست" subtitle="برای همه‌ی همیاران تأییدشده قابل مشاهده است" />
                                <TextField fullWidth multiline rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
                                    placeholder="شرح مراقبت مورد نیاز، شرایط بیمار و نکات مهم را بنویسید. لطفاً شماره تماس، نام یا آدرس دقیق ننویسید — این اطلاعات بعد از پرداخت به همیار داده می‌شود."
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.6)' } }} />
                            </Box>

                            {estimate && (
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5, p: 2, borderRadius: '14px',
                                    bgcolor: brand.accentTint, border: `1px solid ${brand.accent}55`,
                                }}>
                                    <PaymentsIcon sx={{ color: brand.accentDeep }} />
                                    <Typography variant="body2">
                                        بازه‌ی قیمت پیشنهادی: <b>{formatToman(estimate.price_min)}</b> تا{' '}
                                        <b>{formatToman(estimate.price_max)}</b>
                                        {' '}— همیاران معمولاً در همین بازه پیشنهاد می‌دهند.
                                    </Typography>
                                </Box>
                            )}

                            <Button type="submit" fullWidth variant="contained" size="large" disabled={loading}>
                                {loading ? <CircularProgress size={26} color="inherit" /> : 'ثبت درخواست'}
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Container>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default RequestForm;
